/**
 * Whether the new public Arche Store (`/a/store`) is shown in this
 * environment — the Dev Console's store switch (`store_page_enabled`). The
 * header's "Arche Store" link and the store page ask this: while it is off,
 * the Arche Store is the old one (`/marketplace`, the Advanced search) and
 * nothing links to the new one.
 *
 * Not React Query, on purpose: the marketing site's header asks too, and that
 * page renders with no query client. One request per page load, shared by
 * every caller; `null` until it answers. A failed request reads as off — a
 * link to a page that may not be there is worse than the old store.
 */
import { useEffect, useSyncExternalStore } from 'react';
import api from './api';
import { arrivedWithStoreSnapshot } from './store-snapshot-data';

/** How long an answer is trusted before the next caller asks again. */
const FRESH_MS = 5 * 60 * 1000;

/**
 * A page that arrived as a store snapshot starts as "shown": the publisher
 * writes snapshots only while the switch is on and withdraws them when it is
 * turned off (ADR-043). It is still confirmed with the server on mount — the
 * answer counts as never fetched.
 */
let enabled: boolean | null = arrivedWithStoreSnapshot() ? true : null;
let fetchedAt = 0;
let inFlight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function publish(next: boolean): void {
  enabled = next;
  fetchedAt = Date.now();
  for (const listener of listeners) listener();
}

/** Ask the server again (after the switch is flipped, or when the answer is stale). */
export function refreshStoreAvailability(): Promise<void> {
  if (!inFlight) {
    inFlight = Promise.resolve()
      .then(() => api.getStoreAvailability())
      .then((r) => publish(r?.enabled === true))
      .catch(() => publish(false))
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** True when the new Arche Store is shown; false when it is not; null until known. */
export function useStorePageAvailable(): boolean | null {
  const value = useSyncExternalStore(
    subscribe,
    () => enabled,
    () => null,
  );
  useEffect(() => {
    if (enabled === null || Date.now() - fetchedAt > FRESH_MS) void refreshStoreAvailability();
  }, []);
  return value;
}

/** Test hook. */
export function __resetStoreAvailabilityForTests(): void {
  enabled = null;
  fetchedAt = 0;
  inFlight = null;
  listeners.clear();
}
